import React, { useState, useCallback } from 'react';
import '../styles/gallery.css';

interface ImageGalleryProps {
  images: string[];
  pageSize?: number;
}

// Helper: get optimized and original paths
const getOptimizedPath = (src: string) => src.replace('/imageGallery/', '/imageGallery_optimized/');
const getOriginalPath = (src: string) => src.replace('/imageGallery_optimized/', '/imageGallery/');

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, pageSize = 30 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [page, setPage] = useState(1);

  const totalPages = Math.ceil(images.length / pageSize);
  const pagedImages = images.slice((page - 1) * pageSize, page * pageSize);

  const openModal = useCallback((index: number) => {
    setCurrentIndex((page - 1) * pageSize + index);
    setIsOpen(true);
  }, [page, pageSize]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const showPrev = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const showNext = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setIsOpen(false); // Modal kapalı başlasın
  };

  return (
    <>
      <div className="gallery-grid">
        {pagedImages.map((img, idx) => (
          <div key={img} className="gallery-item" onClick={() => openModal(idx)}>
            <img src={getOptimizedPath(img)} alt={`gallery-${idx}`} loading="lazy" />
          </div>
        ))}
        {isOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={closeModal}>&times;</button>
              {images.length > 1 && (
                <img
                  src={getOptimizedPath(images[(currentIndex === 0 ? images.length - 1 : currentIndex - 1)])}
                  alt="Önceki"
                  className="modal-thumb modal-thumb-prev"
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(currentIndex === 0 ? images.length - 1 : currentIndex - 1); }}
                />
              )}
              <button className="modal-prev" onClick={showPrev}>&#8592;</button>
              <img src={getOriginalPath(images[currentIndex])} alt={`modal-${currentIndex}`} className="modal-image" />
              <button className="modal-next" onClick={showNext}>&#8594;</button>
              {images.length > 1 && (
                <img
                  src={getOptimizedPath(images[(currentIndex === images.length - 1 ? 0 : currentIndex + 1)])}
                  alt="Sonraki"
                  className="modal-thumb modal-thumb-next"
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(currentIndex === images.length - 1 ? 0 : currentIndex + 1); }}
                />
              )}
            </div>
          </div>
        )}
      </div>
      {totalPages > 1 && (
        <div className="gallery-pagination">
          <button
            className="gallery-pagination-btn"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            &laquo;
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              className={`gallery-pagination-btn${page === i + 1 ? ' active' : ''}`}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button
            className="gallery-pagination-btn"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
          >
            &raquo;
          </button>
        </div>
      )}
    </>
  );
};

export default ImageGallery; 